/**
 * W4 (field test 2026-10-16) — THE CANONICAL WORKING-DAY / CAPACITY RESOLVER.
 *
 * One answer to "what minutes can this technician work on this shop-local date, and where is the
 * boundary of authorized work?", shared by every scheduling path that adopts it (the lane cascades'
 * EOD detection, the approved-rollover allocation, the decision prompt, the reservation sync and the
 * browser projection). It builds on the planner's existing dated resolver
 * (`rolloverWorkDay.buildTechnicianWorkDayResolver`: per-date hours, time off, holidays, recurring and
 * dated breaks, partial-day absences) and adds the three things that resolver did not know:
 *
 *   1. COMMITMENTS — protected work already on a later day (started-work continuations, waiters,
 *      appointments, pinned rows) is unavailable to a continuation, exactly like a break. A plan built
 *      on `withCommitments(...)` can never sit on top of Monday's existing bookings (W4 defect 5).
 *   2. AUTHORIZED OVERTIME — the allocation boundary is the day's last working minute, or a recorded
 *      per-ticket overtime authorization's until-minute when one exists. The shop's
 *      `willingness_roll_past_eod` is NOT overtime: it only decides WHEN the question is asked (D-6).
 *   3. UNITS (D-U1) — book minutes (job ledger), work minutes (technician wall time incl. reserve),
 *      hands-on labor minutes and calendar working minutes are converted explicitly, never mixed.
 *
 * Pure: no I/O, no clock, no timezone arithmetic (minutes are from shop-local midnight; dates are
 * shop-local `yyyy-MM-dd`; DST is handled by the callers' instant helpers in timezoneMath.ts, which
 * convert a window as start instant + elapsed minutes). Deno and the browser import it.
 */
import {
  DEFAULT_ROLLOVER_SEARCH_DAYS,
  lastWorkingMinute,
  nextCalendarDate,
  type PlannerDay,
  type PlannerInterval,
  type ResolveWorkDay,
} from './rolloverSegmentPlanner.ts';
import { planningBuffer } from './finishIntentDerivation.ts';

export type { PlannerDay, PlannerInterval, ResolveWorkDay };

// ─── minutes ────────────────────────────────────────────────────────────────

/** A working minute: inside [open, close) and not in any unavailable interval. */
export function isWorkingMinute(day: PlannerDay, minute: number): boolean {
  if (minute < day.openMin || minute >= day.closeMin) return false;
  for (const u of day.unavailable) if (minute >= u.start && minute < u.end) return false;
  return true;
}

/** Working minutes in [fromMin, toMin). Breaks, absences and commitments never count. */
export function workingMinutesBetween(day: PlannerDay, fromMin: number, toMin: number): number {
  let n = 0;
  const a = Math.max(Math.floor(fromMin), day.openMin);
  const b = Math.min(Math.ceil(toMin), day.closeMin);
  for (let m = a; m < b; m++) if (isWorkingMinute(day, m)) n++;
  return n;
}

/**
 * The minute at which `workMin` working minutes have elapsed from `fromMin`, bounded by the day's
 * close. `placed < workMin` means the work does not fit today.
 */
export function finishWithinDay(day: PlannerDay, fromMin: number, workMin: number): { finishMin: number; placed: number; startMin: number | null } {
  let placed = 0;
  let startMin: number | null = null;
  let finishMin = Math.max(fromMin, day.openMin);
  const need = Math.max(0, Math.round(workMin));
  for (let m = Math.max(Math.floor(fromMin), day.openMin); m < day.closeMin && placed < need; m++) {
    if (!isWorkingMinute(day, m)) continue;
    if (startMin === null) startMin = m;
    placed++;
    finishMin = m + 1;
  }
  return { finishMin, placed, startMin };
}

/**
 * The HONEST projection of an unbounded finish — what the clock would read if the work simply
 * continued past close (breaks still skipped, minutes after close counted one-for-one). Used only to
 * DETECT and SHOW an overrun; never written as an authorized schedule.
 */
export function projectedFinishUnbounded(day: PlannerDay, fromMin: number, workMin: number): number {
  const need = Math.max(0, Math.round(workMin));
  const inDay = finishWithinDay(day, fromMin, need);
  if (inDay.placed >= need) return need === 0 ? Math.max(fromMin, day.openMin) : inDay.finishMin;
  const start = Math.max(Math.floor(fromMin), day.closeMin);
  return start + (need - inDay.placed);
}

// ─── boundary & tolerance ───────────────────────────────────────────────────

/**
 * The allocation boundary (D-6): the end of the day's last working minute, or — only when a
 * per-ticket overtime authorization exists for this ticket and date — its until-minute.
 */
export function authorizedBoundaryMin(day: PlannerDay, overtimeUntilMin?: number | null): number {
  const normal = lastWorkingMinute(day);
  return typeof overtimeUntilMin === 'number' && Number.isFinite(overtimeUntilMin) && overtimeUntilMin > normal
    ? Math.round(overtimeUntilMin)
    : normal;
}

/** The day with its close moved to an authorized overtime boundary (only ever later; never earlier). */
export function withAuthorizedOvertime(day: PlannerDay, overtimeUntilMin?: number | null): PlannerDay {
  const until = authorizedBoundaryMin(day, overtimeUntilMin);
  return until > day.closeMin ? { ...day, closeMin: until } : day;
}

/**
 * The notification tolerance (D-6): `shop_culture.willingness_roll_past_eod`, in minutes, >= 0.
 * It only decides when the question is ASKED. It never extends the allocation boundary and is not
 * overtime. No cap is applied here (W4 D4 "cap at 30" is not a founder decision).
 */
export function notificationToleranceMin(shopCulture: unknown): number {
  const raw = (shopCulture as Record<string, unknown> | null | undefined)?.willingness_roll_past_eod;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
}

// ─── commitments ────────────────────────────────────────────────────────────

/** Protected work on a later day, as unavailable minutes for a continuation planned onto that day. */
export type CommitmentsByDate = Map<string, PlannerInterval[]> | Record<string, PlannerInterval[]>;

/**
 * A resolver whose days also exclude the given commitments. A continuation planned on it flows
 * around protected bookings the way it flows around breaks (W4 D5: interruptible), so it never
 * displaces a protected appointment or a started-work continuation.
 */
export function withCommitments(resolveDay: ResolveWorkDay, commitments: CommitmentsByDate): ResolveWorkDay {
  const get = (d: string): PlannerInterval[] =>
    (commitments instanceof Map ? commitments.get(d) : commitments[d]) ?? [];
  return (dateStr: string) => {
    const day = resolveDay(dateStr);
    if (!day) return null;
    const extra = get(dateStr).filter((i) => Number.isFinite(i.start) && Number.isFinite(i.end) && i.end > i.start);
    return extra.length === 0 ? day : { ...day, unavailable: [...day.unavailable, ...extra] };
  };
}

/** The first working date strictly after `afterDateStr`, within the horizon; null when none exists. */
export function nextWorkingDate(
  resolveDay: ResolveWorkDay,
  afterDateStr: string,
  horizon = DEFAULT_ROLLOVER_SEARCH_DAYS,
): string | null {
  let d = nextCalendarDate(afterDateStr);
  for (let i = 0; i < horizon; i++) {
    const day = resolveDay(d);
    if (day && workingMinutesBetween(day, day.openMin, day.closeMin) > 0) return d;
    d = nextCalendarDate(d);
  }
  return null;
}

// ─── units (D-U1) ───────────────────────────────────────────────────────────

/**
 * The one buffer rule: a recorded number is used as recorded (0 stays 0); unrecorded is 0 — the
 * server finish authority's `planningBuffer`. Re-exported so every consumer of this resolver reads
 * the reserve the same way.
 */
export function resolvePlanningBufferPct(bufferPct: number | null | undefined): number {
  return planningBuffer(bufferPct);
}

/** Efficiency as a fraction; unknown/invalid is 1.0 (100%), never 0. */
export function efficiencyFactor(efficiencyPct: number | null | undefined): number {
  return typeof efficiencyPct === 'number' && Number.isFinite(efficiencyPct) && efficiencyPct > 0
    ? Math.max(efficiencyPct, 1) / 100
    : 1;
}

/** Book minutes → work (wall) minutes for one technician, reserve included (== calculateWallMinutes). */
export function bookToWorkMinutes(bookMin: number, efficiencyPct: number | null | undefined, bufferPct: number | null | undefined): number {
  return Math.round((bookMin / efficiencyFactor(efficiencyPct)) * (1 + resolvePlanningBufferPct(bufferPct) / 100));
}

/** Hands-on labor minutes → book minutes EARNED, at the producing technician's efficiency. */
export function laborToEarnedBook(laborMin: number, efficiencyPct: number | null | undefined): number {
  return Math.max(0, laborMin) * efficiencyFactor(efficiencyPct);
}

// ─── the resolved day ───────────────────────────────────────────────────────

export type SchedulingDayState = 'working' | 'not_working';

export interface SchedulingDay {
  state: SchedulingDayState;
  dateStr: string;
  /** Null when not working. Includes commitments when the resolver was wrapped with them. */
  day: PlannerDay | null;
  /** Allocation boundary (last working minute, or authorized overtime). Null when not working. */
  boundaryMin: number | null;
  /** Working minutes in the whole day (before any commitment of this ticket's own lane). */
  capacityMin: number;
}

export function resolveSchedulingDay(
  resolveDay: ResolveWorkDay,
  dateStr: string,
  opts: { overtimeUntilMin?: number | null } = {},
): SchedulingDay {
  const raw = resolveDay(dateStr);
  if (!raw || raw.closeMin <= raw.openMin) {
    return { state: 'not_working', dateStr, day: null, boundaryMin: null, capacityMin: 0 };
  }
  const day = withAuthorizedOvertime(raw, opts.overtimeUntilMin);
  const capacityMin = workingMinutesBetween(day, day.openMin, day.closeMin);
  if (capacityMin <= 0) return { state: 'not_working', dateStr, day: null, boundaryMin: null, capacityMin: 0 };
  return { state: 'working', dateStr, day, boundaryMin: authorizedBoundaryMin(raw, opts.overtimeUntilMin), capacityMin };
}
